<template>
  <div class="bg-white dark:bg-[#0c1427]">
    
    <div class="chat-content-wrapper">
      <div class="lg:grid lg:grid-cols-3 gap-[25px] h-full overflow-hidden">
        <div class="lg:col-span-1 h-full overflow-hidden" :class="{ 'hidden': selectedChat && isMobile }">
          <Sidebar ref="sidebarRef" @chat-selected="selectChat" @chats-loaded="handleChatsLoaded" />
        </div>
        <div class="lg:col-span-2 h-full overflow-hidden" :class="{ 'hidden': !selectedChat && isMobile }">
          <ChatPanel 
            :selected-chat="selectedChat || undefined" 
            @back-to-list="backToList" 
            @update-bot-status="handleBotStatusUpdate"
            @assignment-updated="handleAssignmentUpdate"
            @new-message-received="handleNewMessageReceived"
          />
        </div>
      </div>
    </div>
  </div>
  <AddEventModal />
  <EditEventModal ref="editEventModalRef" />
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import Sidebar from "@/components/Apps/Chat/Sidebar.vue";
import ChatPanel from "@/components/Apps/Chat/index.vue";
import AddEventModal from "@/components/Apps/Calendar/AddEventModal.vue";
import EditEventModal from "@/components/Apps/Calendar/EditEventModal.vue";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

// i18n setup
const { locale } = useI18n();

// Translation files
const translationFiles = {
  en: enTranslations,
  he: heTranslations,
  ar: arTranslations
};

// Direct translation function that bypasses vue-i18n
const directT = (key: string): string => {
  const parts = key.split('.');
  let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
  
  for (const part of parts) {
    if (!result || !(part in result)) {
      console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
      // Fallback to English if translation not found
      let enResult: TranslationObject = translationFiles.en;
      for (const fallbackPart of parts) {
        if (!enResult || !(fallbackPart in enResult)) return key;
        enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
      }
      return typeof enResult === 'string' ? enResult : key;
    }
    result = result[part as keyof typeof result] as TranslationObject;
  }
  return typeof result === 'string' ? result : key;
};

// הגדרת ממשק לצ'אט
interface ChatData {
  id: string | number;
  name: string;
  image: string;
  lastMessage?: string;
  lastTime?: string;
  unread?: number;
  [key: string]: any; // לשדות נוספים שיכולים להיות
}

const editEventModalRef = ref<any>(null);
const sidebarRef = ref<any>(null);
const selectedChat = ref<ChatData | null>(null);
const isMobile = ref(false);

// בדיקה אם המכשיר הוא מובייל
const checkIfMobile = (): void => {
  isMobile.value = window.innerWidth < 1024; // lg breakpoint in Tailwind
};

// בחירת צ'אט ומעבר לתצוגת צ'אט במובייל
const selectChat = (chat: ChatData): void => {
  selectedChat.value = chat;
};

// חזרה לרשימת הצ'אטים
const backToList = (): void => {
  if (isMobile.value) {
    selectedChat.value = null;
  }
};

// עקיבה אחר שינויי גודל המסך
onMounted(() => {
  checkIfMobile();
  window.addEventListener('resize', checkIfMobile);
  // Add chat-page class to body for mobile styling
  document.body.classList.add('chat-page');
});

onUnmounted(() => {
  window.removeEventListener('resize', checkIfMobile);
  // Remove chat-page class when leaving the page
  document.body.classList.remove('chat-page');
});

function openEditEventModal(event: any): void {
  if (editEventModalRef.value) {
    editEventModalRef.value.openModal(event);
  }
}

// Handle the update-bot-status event
function handleBotStatusUpdate(data: { threadId: string | number, botstatus: boolean }): void {
  console.log('Updating bot status in parent:', data);
  if (selectedChat.value && selectedChat.value.id === data.threadId) {
    // Create a new object to avoid mutating the original
    const updatedChat = Object.assign({}, selectedChat.value);
    updatedChat.botstatus = data.botstatus;
    selectedChat.value = updatedChat;
    console.log('Bot status updated in parent component:', selectedChat.value.botstatus);
  }
}

// Handle the chats-loaded event - auto-select first chat on desktop
function handleChatsLoaded(chats: ChatData[]): void {
  console.log('Chats loaded in parent component', { count: chats.length });
  
  // Auto-select the first chat only on desktop (not mobile)
  if (!isMobile.value && chats.length > 0 && !selectedChat.value) {
    console.log('Auto-selecting first chat on desktop');
    selectChat(chats[0]);
  }
}

// Handle assignment updates
function handleAssignmentUpdate(data: { chatId: string | number, assignedUserId: string | null, assignedUserName: string }): void {
  console.log('Assignment updated in parent:', data);
  
  // Update the sidebar chat list
  if (sidebarRef.value && sidebarRef.value.updateChatAssignment) {
    sidebarRef.value.updateChatAssignment(data.chatId, data.assignedUserId, data.assignedUserName);
  }
}

// Handle new message received from any chat in the organization
function handleNewMessageReceived(data: { message: any, phone: string, threadId: string | null }): void {
  console.log('🎯 ChatPage: New message received for organization:', data);
  console.log('📝 Message preview will be updated in sidebar');
  
  // Update the sidebar with the new message information
  if (sidebarRef.value && sidebarRef.value.updateChatWithNewMessage) {
    console.log('📡 Calling sidebar updateChatWithNewMessage');
    sidebarRef.value.updateChatWithNewMessage(data);
    console.log('✅ Sidebar update completed');
  } else {
    console.log('❌ Sidebar ref not available or method not found');
    console.log('sidebarRef.value:', !!sidebarRef.value);
    console.log('updateChatWithNewMessage method:', !!(sidebarRef.value?.updateChatWithNewMessage));
  }
}
</script>

<style scoped>
/* Chat Page Container */
.chat-page-container {
  min-height: 100vh;
}



.chat-content-wrapper {
  /* חישוב גובה שמתחשב ב-header ו-padding של main-content */
  height: calc(100vh - 80px); /* 80px = padding-top של main-content */
  display: flex;
  flex-direction: column;
  padding: 24px;
  box-sizing: border-box;
  overflow: hidden;
}

@media (min-width: 768px) {
  .chat-content-wrapper {
    height: calc(100vh - 95px); /* 95px = padding-top במסכים בינוניים */
  }
}

.dark .background-overlay {
  background: linear-gradient(135deg, 
    rgba(59, 130, 246, 0.12) 0%,
    rgba(168, 85, 247, 0.1) 25%,
    rgba(236, 72, 153, 0.12) 50%,
    rgba(34, 197, 94, 0.1) 75%,
    rgba(249, 115, 22, 0.12) 100%);
}

.dark .background-gradient {
  background: radial-gradient(ellipse at top left, 
    rgba(59, 130, 246, 0.2) 0%, 
    transparent 50%),
    radial-gradient(ellipse at bottom right, 
    rgba(168, 85, 247, 0.18) 0%, 
    transparent 50%),
    radial-gradient(ellipse at center, 
    rgba(34, 197, 94, 0.15) 0%, 
    transparent 70%);
}

.dark .background-pattern {
  background-image: 
    radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.25) 0%, transparent 2%),
    radial-gradient(circle at 75% 75%, rgba(168, 85, 247, 0.2) 0%, transparent 2%),
    radial-gradient(circle at 50% 50%, rgba(34, 197, 94, 0.18) 0%, transparent 1.5%),
    radial-gradient(circle at 10% 80%, rgba(236, 72, 153, 0.15) 0%, transparent 2%),
    radial-gradient(circle at 90% 20%, rgba(249, 115, 22, 0.15) 0%, transparent 2%);
}

/* Enhanced shadows for chat components */
.lg\:grid > div {
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
}

/* Enhanced shadow for chat sidebar (first component) */
.lg\:grid > div:first-child {
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
  background: #ffffff;
}

/* Dark mode enhanced shadows */
.dark .lg\:grid > div {
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.3),
    0 2px 4px -1px rgba(0, 0, 0, 0.2);
  border: 1px solid #2a3441;
  background: #15203c;
}

/* Enhanced dark mode shadow for chat sidebar */
.dark .lg\:grid > div:first-child {
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.3),
    0 2px 4px -1px rgba(0, 0, 0, 0.2);
  border: 1px solid #2a3441;
  background: #15203c;
}



/* Mobile adjustments */
@media (max-width: 1024px) {
  .chat-content-wrapper {
    padding: 16px;
  }
  
  .lg\:grid > div {
    box-shadow: 
      0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border: 1px solid #e5e7eb;
    background: #ffffff;
  }
  
  .dark .lg\:grid > div {
    border: 1px solid #2a3441;
    background: #15203c;
  }
}

  @media (max-width: 768px) {
    .chat-content-wrapper {
      padding: 0;
      padding-top: 0px;
    }
    
    /* Square corners for chat containers on mobile */
    .lg\:grid > div {
      border-radius: 0 !important;
    }
  }
</style>

    